/**
 * The module's vocabularies, restated for TypeScript.
 *
 * These mirror `core/store/types.py`, which itself deliberately restates the SQLAlchemy
 * enums rather than importing them (invariant 4 — in remote mode there is no model to
 * import, only JSON on the wire). The Python side pins its restatement against the CHECK
 * constraints in `tests/unit/test_vocabularies.py`; the equivalent guard on this side is
 * that every response is parsed through these schemas, so a value the server starts
 * sending that is not listed here fails loudly at the boundary instead of flowing into a
 * `switch` that quietly falls through.
 *
 * Adding a member is a contract change. Bump `CONTRACT_VERSION` in `../version.ts`.
 */
import { z } from 'zod';
export const storageModeSchema = z.enum(['local', 'remote']);
export const itemTypeSchema = z.enum(['STOCK', 'GOODS', 'SERVICE', 'NON_STOCK', 'BUNDLE']);
export const valuationMethodSchema = z.enum(['WAC', 'FIFO', 'STANDARD']);
export const trackingPolicySchema = z.enum(['NONE', 'BATCH', 'SERIAL']);
export const uomDimensionSchema = z.enum(['COUNT', 'MASS', 'VOLUME', 'LENGTH', 'AREA', 'TIME']);
export const barcodeTypeSchema = z.enum([
    'EAN13',
    'EAN8',
    'UPCA',
    'CODE128',
    'GS1_128',
    'QR',
    'INTERNAL',
]);
export const zoneTypeSchema = z.enum(['STORAGE', 'PICK', 'RECEIVING', 'SHIPPING', 'QUARANTINE']);
export const binTypeSchema = z.enum(['STORAGE', 'PICK', 'BULK', 'STAGING', 'QUARANTINE']);
export const lifecycleStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
export const stockStateSchema = z.enum(['AVAILABLE', 'IN_TRANSIT', 'QUARANTINE']);
export const movementTypeSchema = z.enum([
    'OPENING',
    'RECEIPT',
    'ISSUE',
    'ADJUSTMENT_IN',
    'ADJUSTMENT_OUT',
    'TRANSFER_OUT',
    'TRANSFER_IN',
]);
/**
 * Which way each movement type moves stock.
 *
 * The server constrains the `(movement_type, direction)` pair in the database, so a
 * RECEIPT that decreases stock is unrepresentable. This map exists only so a skin can
 * render a sign without a second `switch`; it never travels on the wire — `direction` is
 * derived server side and `MovementCreate` has no such field.
 */
export const MOVEMENT_DIRECTION = Object.freeze({
    OPENING: 1,
    RECEIPT: 1,
    ISSUE: -1,
    ADJUSTMENT_IN: 1,
    ADJUSTMENT_OUT: -1,
    TRANSFER_OUT: -1,
    TRANSFER_IN: 1,
});
export const reservationModeSchema = z.enum(['SOFT', 'HARD']);
export const reservationStatusSchema = z.enum(['ACTIVE', 'CONSUMED', 'RELEASED', 'EXPIRED']);
export const countTypeSchema = z.enum(['CYCLE', 'FULL']);
export const countStatusSchema = z.enum([
    'DRAFT',
    'FROZEN',
    'COUNTING',
    'SUBMITTED',
    'APPROVED',
    'REJECTED',
    'CANCELLED',
]);
export const countVarianceTypeSchema = z.enum(['MATCH', 'SHORT', 'OVER', 'NOT_COUNTED']);
export const suggestionStatusSchema = z.enum(['SUGGESTED', 'CONVERTED', 'DISMISSED']);
/**
 * Feature codes from `core/entitlements/catalog.py`.
 *
 * A skin uses these to decide what to *render*. They are not a permission check —
 * invariant 7 means the server checks again on every gated route, and a 403 arriving
 * anyway is a normal outcome, not a bug in this list.
 */
export const FEATURES = {
    itemMaster: 'item_master',
    stockLedger: 'stock_ledger',
    stockEnquiry: 'stock_enquiry',
    goodsIssue: 'goods_issue',
    multiWarehouse: 'multi_warehouse',
    stockTransfer: 'stock_transfer',
    procurement: 'procurement',
    reorderPoint: 'reorder_point',
    stockReservation: 'stock_reservation',
    batchSerial: 'batch_serial',
    expiryManagement: 'expiry_management',
    cycleCounting: 'cycle_counting',
    valuationFifo: 'valuation_fifo',
    landedCost: 'landed_cost',
    manufacturing: 'manufacturing',
    retailBranch: 'retail_branch',
    fieldService: 'field_service',
    batchRecall: 'batch_recall',
    consignment: 'consignment',
};
export const PLAN_ORDER = ['essential', 'growth', 'professional', 'enterprise'];
//# sourceMappingURL=enums.js.map